import React, { useState, useEffect, useRef } from 'react';
import { Box, IconButton, Menu, MenuItem, Typography } from '@mui/material';
import { FaPlus, FaEllipsisV } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { dashboardsApi } from '../../../../../services/api';
import { useAlert } from '../../../../../contexts/AlertContext';
import LoadingSpinner from '../../../../common/LoadingSpinner';
import Modal from '../../../../common/Modal';
import ConfirmationModal from '../../../../common/ConfirmationModal';
import EmptyState from '../../../../common/EmptyState';
import { DashboardsGrid, DashboardCard, DashboardCardContent, DashboardName, DashboardDescription } from './styles';
import { FormContainer } from '../../styles';
import { HeaderButton } from '../../../../common/styles';
import DashboardForm from './components/DashboardForm';

const Dashboards = ({ projectId, readOnly = false }) => {
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const [dashboards, setDashboards] = useState([]);
  const [isDashboardsLoading, setIsDashboardsLoading] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedDashboard, setSelectedDashboard] = useState(null);
  const [showDashboardModal, setShowDashboardModal] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [isFormValid, setIsFormValid] = useState(false);
  const dashboardFormRef = useRef(null);

  useEffect(() => {
    if (projectId) {
      fetchDashboards();
    }
  }, [projectId]);

  const fetchDashboards = async () => {
    try {
      setIsDashboardsLoading(true);
      const dashboardsData = await dashboardsApi.getDashboardsByProjectId(projectId);
      setDashboards(dashboardsData);
    } catch (err) {
      showAlert(err.message || 'Failed to fetch dashboards', 'error', err);
    } finally {
      setIsDashboardsLoading(false);
    }
  };

  const handleMenuOpen = (event, dashboard) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setSelectedDashboard(dashboard);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedDashboard(null);
  };

  const handleEditDashboard = () => {
    handleMenuClose();
    setSelectedDashboard(selectedDashboard);
    setShowDashboardModal(true);
  };

  const handleDeleteClick = () => {
    setItemToDelete({ id: selectedDashboard.id, name: selectedDashboard.name });
    setShowDeleteConfirmation(true);
    handleMenuClose();
  };

  const handleConfigureDashboard = () => {
    navigate(`/dashboard-config/${selectedDashboard.id}`);
    handleMenuClose();
  };

  const handleDeleteConfirm = async () => {
    try {
      await dashboardsApi.deleteDashboard(itemToDelete.id);
      showAlert('Dashboard deleted successfully', 'success');
      fetchDashboards();
    } catch (err) {
      showAlert(err.message || 'Failed to delete dashboard', 'error', err);
    } finally {
      setShowDeleteConfirmation(false);
      setItemToDelete(null);
    }
  };

  const handleAddDashboard = () => {
    setSelectedDashboard(null);
    setShowDashboardModal(true);
  };

  const handleDashboardSubmit = async (dashboardData) => {
    try {
      if (selectedDashboard?.id) {
        await dashboardsApi.updateDashboard(selectedDashboard.id, {
          ...dashboardData,
          id: selectedDashboard.id
        });
        showAlert('Dashboard updated successfully', 'success');
      } else {
        await dashboardsApi.createDashboard(dashboardData);
        showAlert('Dashboard created successfully', 'success');
      }
      
      setShowDashboardModal(false);
      fetchDashboards();
    } catch (err) {
      showAlert(err.message || 'Failed to save dashboard', 'error', err);
    }
  };

  const handleModalSubmit = () => {
    if (dashboardFormRef.current) {
      dashboardFormRef.current.handleSubmit();
    }
  };

  if (isDashboardsLoading) {
    return <LoadingSpinner message="Loading dashboards..."/>;
  }

  return (
    <FormContainer>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <div style={{ 
          color: '#64748B', 
          fontSize: '14px',
          display: 'flex',
          alignItems: 'center',
          gap: '4px'
        }}>
          <span style={{ fontWeight: 600 }}>
            {dashboards.length}
          </span>
          {dashboards.length === 1 ? 'dashboard' : 'dashboards'} found
        </div>

        {!readOnly && (
          <HeaderButton onClick={handleAddDashboard}>
            <FaPlus /> Add Dashboard
          </HeaderButton>
        )}
      </Box>

      {!dashboards.length ? (
        <EmptyState
          title="No Dashboards Found"
          description="There are no dashboards in this project yet. Click the 'Add Dashboard' button to create one."
        />
      ) : (
        <DashboardsGrid>
          {dashboards.map((dashboard) => (
            <DashboardCard key={dashboard.id}>
              <DashboardCardContent>
                <DashboardName>
                  {dashboard.name}
                  {!readOnly && (
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, dashboard)}
                  >
                      <FaEllipsisV />
                    </IconButton>
                  )}
                </DashboardName>
                <DashboardDescription>
                  {dashboard.description}
                </DashboardDescription>
              </DashboardCardContent>
            </DashboardCard>
          ))}
        </DashboardsGrid>
      )}

      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleEditDashboard}>
          Edit
        </MenuItem>
        <MenuItem onClick={handleConfigureDashboard}>
          Configure
        </MenuItem>
        <MenuItem onClick={handleDeleteClick} sx={{ color: '#DC2626' }}>
          Delete
        </MenuItem>
      </Menu>

      <Modal
        isOpen={showDashboardModal}
        onClose={() => setShowDashboardModal(false)}
        title={selectedDashboard?.id ? 'Edit Dashboard' : 'Add Dashboard'}
        onSubmit={handleModalSubmit}
        submitText={selectedDashboard?.id ? 'Save Changes' : 'Create Dashboard'}
        submitDisabled={!isFormValid}
        width="600px"
      >
        <DashboardForm
          ref={dashboardFormRef}
          mode={selectedDashboard?.id ? 'edit' : 'add'}
          dashboard={selectedDashboard}
          projectId={projectId}
          onSubmit={handleDashboardSubmit}
          onCancel={() => setShowDashboardModal(false)}
          onValidityChange={setIsFormValid}
        />
      </Modal>

      <ConfirmationModal
        isOpen={showDeleteConfirmation}
        onClose={() => {
          setShowDeleteConfirmation(false);
          setItemToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Delete Dashboard"
        message={`Are you sure you want to delete "${itemToDelete?.name}"? This action cannot be undone.`}
      />
    </FormContainer>
  );
};

export default Dashboards;
